import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { NOTICE_ACT } from "@/lib/notice-tier"

interface GoNoGoAiSuggestionBarProps {
  /** Criteria the AI scored at all — what the headline claims. */
  suggestedCount: number
  /** Suggestions not currently in use — what the button offers to apply. */
  unappliedCount: number
  totalCount: number
  /** Criteria the AI assessed but declined to score, still unscored by the user. */
  needsManualCount: number
  isApplying: boolean
  onApplyAll: () => void
}

/**
 * Bulk-accept bar for AI-suggested Go/No-Go scores — the Win Themes pending-bar
 * pattern applied to scores. There is deliberately no "Discard all": suggestions
 * stay on the criteria as provenance (rationale, confidence, sources) even after
 * they are applied, so there is nothing to throw away.
 *
 * Weight is deliberately low: this is the `NOTICE_ACT` tier (see
 * `@/lib/notice-tier` for why the accent is an edge and never a fill). The
 * action is a white pill: on a tinted surface
 * white is the brightest thing available, so it outranks its neighbours without
 * borrowing the page's one filled-primary slot.
 */
export function GoNoGoAiSuggestionBar({
  suggestedCount,
  unappliedCount,
  totalCount,
  needsManualCount,
  isApplying,
  onApplyAll,
}: GoNoGoAiSuggestionBarProps) {
  return (
    <div className={cn(NOTICE_ACT, 'flex items-center justify-between gap-4')}>
      <div className="min-w-0">
        <div className="text-sm font-medium">
          {suggestedCount} of {totalCount} criteria have suggested scores
        </div>
        <div className="text-meta mt-0.5">
          {needsManualCount > 0
            ? `${needsManualCount} ${needsManualCount === 1 ? 'has' : 'have'} no supporting evidence and ${needsManualCount === 1 ? 'needs' : 'need'} your judgement.`
            : 'Apply them all, or use the score on any criterion individually.'}
        </div>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={onApplyAll}
        disabled={isApplying}
        className="shrink-0 bg-card shadow-sm font-medium"
      >
        {isApplying
          ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Applying…</>
          : `Apply all ${unappliedCount}`}
      </Button>
    </div>
  )
}
